import { useEffect } from 'react';
import './keyboard.css';

export type KeyTone = 'correct' | 'present' | 'absent' | 'used' | 'revealed';

interface Props {
  /** Per-letter color tone (Wordle-style). Letter must be uppercase. */
  letterTones?: Record<string, KeyTone | undefined>;
  /** Letters that should appear disabled (already used / revealed). */
  disabledLetters?: Set<string>;
  onLetter: (letter: string) => void;
  onDelete?: () => void;
  onSubmit?: () => void;
  /** Hide the delete key (e.g. for Emoji Anagram). */
  showDelete?: boolean;
  /** Hide the enter key (e.g. for Emoji Anagram). */
  showSubmit?: boolean;
  submitEnabled?: boolean;
  hintEnabled?: boolean;
  onHint?: () => void;
}

const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];

export default function Keyboard({
  letterTones,
  disabledLetters,
  onLetter,
  onDelete,
  onSubmit,
  showDelete = true,
  showSubmit = true,
  submitEnabled = true,
  hintEnabled,
  onHint,
}: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (/^[a-zA-Z]$/.test(key)) {
        onLetter(key.toUpperCase());
      } else if (key === 'Backspace' && showDelete) {
        onDelete?.();
      } else if (key === 'Enter' && showSubmit) {
        if (submitEnabled) onSubmit?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onLetter, onDelete, onSubmit, showDelete, showSubmit, submitEnabled]);

  return (
    <div className="kb">
      {ROWS.map((row, rowIdx) => (
        <div key={rowIdx} className="kb-row">
          {rowIdx === 2 && showDelete && (
            <button
              type="button"
              className="kb-key kb-key-wide"
              onClick={() => onDelete?.()}
              aria-label="Delete"
            >
              ⌫
            </button>
          )}
          {row.split('').map((letter) => {
            const tone = letterTones?.[letter];
            const disabled = disabledLetters?.has(letter);
            return (
              <button
                key={letter}
                type="button"
                className={
                  'kb-key' + (tone ? ' kb-tone-' + tone : '') + (disabled ? ' is-disabled' : '')
                }
                onClick={() => onLetter(letter)}
                disabled={disabled}
              >
                {letter}
              </button>
            );
          })}
          {rowIdx === 2 && showSubmit && (
            <button
              type="button"
              className="kb-key kb-key-wide kb-key-submit"
              onClick={() => onSubmit?.()}
              disabled={!submitEnabled}
              aria-label="Submit"
            >
              ↵
            </button>
          )}
        </div>
      ))}
      {onHint && (
        <div className="kb-row kb-row-actions">
          <button
            type="button"
            className="kb-action"
            onClick={onHint}
            disabled={!hintEnabled}
          >
            💡 Hint
          </button>
        </div>
      )}
    </div>
  );
}
